import {EActionType} from 'coreHooks';
import {EAddToCartType, IAddProductToCart} from 'coreStore/data/ui/types';

import {useMinicartCount} from 'brandComponents/MinicartCount';

import {useAfterAction} from './useAfterAction';
import {useBasket} from './useBasket';
import {useWishlist} from './useWishlist';

export type TAddToCartItem = Pick<IAddProductToCart, 'variationGroupId' | 'size' | 'quantity' | 'source'> & {
	productId: string;
	type: EAddToCartType.ADD_TO_CART | EAddToCartType.BUY_NOW | EAddToCartType.MOVE_FROM_WISHLIST;
};

/**
 * Adds a product (variant chosen) to the basket: add to cart, buy now (then checkout) or move from the
 * wishlist, with the success or error notification. Call the returned function inside an action or a
 * transition: the minicart count shows the new items while the request runs (`MinicartCountProvider`).
 *
 * @returns {Function} - Resolves to `true` once added, `false` on error (already notified)
 */
export const useAddToCart = () => {
	const basket = useBasket();
	const wishlist = useWishlist();
	const afterAction = useAfterAction();
	const {addOptimisticItems} = useMinicartCount();

	return async ({productId, variationGroupId, size, quantity, type}: TAddToCartItem): Promise<boolean> => {
		const item = {productId, c_variationGroupId: variationGroupId, c_size: size, quantity};

		const isMoveFromWishlist = type === EAddToCartType.MOVE_FROM_WISHLIST;
		const wishlistItem = isMoveFromWishlist ? wishlist.items?.find((wishlistItem) => wishlistItem.productId === productId) : null;

		if (isMoveFromWishlist && !wishlistItem) {
			afterAction(EActionType.CART_ERROR);

			return false;
		}

		const request = () => isMoveFromWishlist
			? basket.moveWishlistItemToBasket({...item, id: wishlistItem.id})
			: basket.addItemToBasket([item]);

		addOptimisticItems(quantity);

		try {
			await request();
		} catch {
			afterAction(EActionType.CART_ERROR);

			return false;
		}

		if (type === EAddToCartType.BUY_NOW) {
			window.location.href = basket.checkoutUrl;
		} else {
			afterAction(EActionType.CART_ADDED);
		}

		return true;
	};
};
